<script lang="ts" setup>
const loadingInvoices = ref(false)
const loadingRenew = ref(false)
const loadingUpdate = ref(false)

const { createToast } = useToasts()
const { openPortal } = useSubscription()
const { t } = useI18n()
const billingStore = useBillingStore()
const teamsStore = useTeamsStore()

const amountInCents = computed(() => {
  if (billingStore.subscription) {
    let discount = 1

    if (billingStore.subscription.discount?.coupon.percent_off) {
      discount = (100 - billingStore.subscription.discount.coupon.percent_off) / 100
    }

    return billingStore.subscription.items.data[0].price.unit_amount! * discount
  }

  return 0
})

const details = computed(() => {
  const details = []

  if (isPaymentOverdue.value) {
    details.push({
      click: () => onOpenPortal('updateSubscription'),
      color: 'red',
      icon: getIcon('creditCard'),
      label: t('details.paymentFailed'),
    })
  }

  if (billingStore.subscription?.cancel_at) {
    details.push({
      color: 'indigo',
      icon: getIcon('check'),
      label: t('details.cancelAt', { date: humanize(billingStore.subscription?.cancel_at * 1000) }),
    })
  }
  else if (billingStore.subscription?.current_period_end && billingStore.subscriptionName !== 'free') {
    details.push({
      icon: getIcon('refresh'),
      label: t('details.renews', {
        amount: amountInCents.value / 100,
        date: humanize(billingStore.subscription?.current_period_end * 1000),
        last4: billingStore.defaultPaymentMethod?.card?.last4,
      }),
    })
  }

  if (billingStore.subscriptionName === 'free') {
    details.push(
      {
        icon: getIcon('cancelAnytime'),
        label: t('details.cancelAnytime'),
      },
      {
        icon: getIcon('companies'),
        label: t('details.customDatapoint'),
      },
      {
        icon: getIcon('dedicatedSupport'),
        label: t('details.dedicatedSupport'),
      },
    )
  }
  else {
    details.push({
      click: () => toggleChatbox('open'),
      icon: getIcon('supportChat'),
      label: t('details.support'),
    })
  }

  return details
})

const isPaymentOverdue = computed(() => {
  return !billingStore.isSubscriptionActive && billingStore.subscriptionName !== 'free'
})

onMounted(async () => {
  await billingStore.fetchPaymentMethods()
})

async function onRenewPlan() {
  try {
    loadingRenew.value = true

    await billingStore.renewSubscription()

    createToast({ message: 'subscriptionRenewed' })
  }
  catch (error) {
    console.error(error)
  }

  loadingRenew.value = false
}

async function onOpenPortal(action: 'downloadInvoices' | 'updateSubscription') {
  try {
    if (action === 'downloadInvoices') {
      loadingInvoices.value = true

      await openPortal()
    }
    else {
      loadingUpdate.value = true

      await openPortal()
    }
  }
  catch (error) {
    console.error(error)
  }

  loadingInvoices.value = false
  loadingUpdate.value = false
}
</script>

<template>
  <UCard>
    <div class="flex flex-col gap-6 text-sm md:flex-row md:text-base">
      <div class="flex flex-1 flex-col gap-4">
        <div class="font-bold">
          <p class="mb-0.5 text-xs text-gray-600 md:text-sm dark:text-gray-400">
            {{ t('currentPlan') }}
          </p>

          <p class="text-2xl md:text-3xl">
            {{ t(`plans.${billingStore.subscriptionName}`) }}
          </p>
        </div>

        <ul class="flex flex-col gap-4">
          <li
            v-for="(detail, index) in details"
            :key="index"
            class="flex items-start"
            :class="{
              'cursor-pointer hover:underline': detail.click,
              'text-indigo-500 dark:text-indigo-400': detail.color === 'indigo',
              'text-red-500 dark:text-red-400': detail.color === 'red',
            }"
            @click="detail.click"
          >
            <UIcon
              class="mr-2 mt-0.5 size-5 flex-shrink-0"
              :name="detail.icon"
            />

            <p>
              {{ detail.label }}
            </p>
          </li>
        </ul>

        <div class="flex flex-wrap gap-4">
          <UButton
            v-if="isPaymentOverdue"
            block
            class="md:w-auto"
            color="red"
            :icon="getIcon('creditCard')"
            :label="t('actions.payOverdueInvoice')"
            :loading="loadingInvoices"
            @click="onOpenPortal('updateSubscription')"
          />

          <UButton
            v-if="teamsStore.currentTeam?.stripeSubscribed"
            block
            class="md:w-auto"
            color="gray"
            :icon="getIcon('download')"
            :label="t('actions.downloadInvoices')"
            :loading="loadingInvoices"
            :ui="{ icon: { base: 'text-indigo-600 dark:text-indigo-400' } }"
            @click="onOpenPortal('downloadInvoices')"
          />

          <UButton
            v-if="billingStore.subscriptionName !== 'free'"
            block
            class="md:w-auto"
            color="gray"
            :icon="getIcon('edit')"
            :label="t('actions.updateSubscription')"
            :loading="loadingUpdate"
            :ui="{ icon: { base: 'text-grayM-600 dark:text-grayM-400' } }"
            @click="onOpenPortal('updateSubscription')"
          />

          <UButton
            v-if="!billingStore.subscription?.cancel_at && billingStore.subscriptionName !== 'free' && billingStore.isSubscriptionActive"
            block
            class="md:w-auto"
            color="gray"
            :icon="getIcon('close')"
            :label="t('actions.cancelPlan')"
            :ui="{ icon: { base: 'text-red-600 dark:text-red-400' } }"
            @click="triggerChatbox"
          />

          <UButton
            v-if="billingStore.subscription?.cancel_at && billingStore.isSubscriptionActive"
            block
            class="md:w-auto"
            color="gray"
            :icon="getIcon('refresh')"
            :label="t('actions.renewPlan')"
            :loading="loadingRenew"
            :ui="{ icon: { base: 'text-green-600 dark:text-green-400' } }"
            @click="onRenewPlan"
          />
        </div>
      </div>

      <SubscriptionFormCredits
        v-if="billingStore.subscriptionName !== 'free'"
        class="md:w-80 lg:w-96"
      />
    </div>
  </UCard>
</template>

<i18n lang="json">
{
  "en": {
    "actions": {
      "cancelPlan": "Cancel plan",
      "downloadInvoices": "Download invoices",
      "payOverdueInvoice": "Pay overdue invoice",
      "renewPlan": "Renew plan",
      "updateSubscription": "Update subscription"
    },
    "currentPlan": "Current plan",
    "details": {
      "cancelAnytime": "You can cancel your subscription at any time.",
      "cancelAt": "Will cancel automatically {date}.",
      "customDatapoint": "Need a specific feature or company datapoint? Let us know and we'll add it for you.",
      "dedicatedSupport": "Enjoy our dedicated support to help you with any questions.",
      "paymentFailed": "Payment failed, click here to pay the invoice.",
      "renews": "Renew {date} for ${amount} with card ending in •••• {last4}.",
      "support": "If you need any help, feel free to chat with us."
    }
  },
  "es": {
    "actions": {
      "cancelPlan": "Cancelar plan",
      "downloadInvoices": "Descargar facturas",
      "payOverdueInvoice": "Pagar factura impagada",
      "renewPlan": "Renovar plan",
      "updateSubscription": "Actualizar suscripción"
    },
    "currentPlan": "Plan actual",
    "details": {
      "cancelAnytime": "Puedes cancelar tu suscripción en cualquier momento.",
      "cancelAt": "Se cancelará automáticamente {date}.",
      "customDatapoint": "¿Necesitas una función o un punto de datos específico? Háznoslo saber y lo añadiremos para ti.",
      "dedicatedSupport": "Disfruta de nuestro soporte dedicado para ayudarte con cualquier pregunta.",
      "paymentFailed": "Pago fallido, haz clic aquí para pagar la factura.",
      "renews": "Renueva {date} por ${amount} con tarjeta terminada en •••• {last4}.",
      "support": "Si necesitas cualquier ayuda, no dudes en chatear con nosotros."
    }
  },
  "fr": {
    "actions": {
      "cancelPlan": "Annuler l'abonnement",
      "downloadInvoices": "Téléchargez les factures",
      "payOverdueInvoice": "Régler la facture impayée",
      "renewPlan": "Renouveler",
      "updateSubscription": "Mettre à jour"
    },
    "currentPlan": "Abonnement actuel",
    "details": {
      "cancelAnytime": "Vous pouvez annuler votre abonnement à tout moment.",
      "cancelAt": "S'annulera automatiquement {date}.",
      "customDatapoint": "Besoin d'une fonctionnalité ou d'un datapoint spécifique ? Dites-le nous et nous l'ajouterons.",
      "dedicatedSupport": "Profitez de notre support dédié pour vous aider avec toutes vos questions.",
      "paymentFailed": "Paiement échoué, cliquez ici pour payer la facture.",
      "renews": "Renouvelle {date} pour ${amount} avec la carte •••• {last4}.",
      "support": "Si vous avez besoin d'aide, n'hésitez pas à discuter avec nous."
    }
  }
}
</i18n>
